import { useEffect, useMemo, useState } from "react";
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  fetchReportConcentration,
  fetchReportRisks,
  fetchReportSummary,
  fetchReportTrends,
  fetchReportWatchlists,
} from "../api/reports";
import FilterBar from "../components/FilterBar";
import Table from "../components/Table";

const CHART_THEME = {
  primary: "#4c6fff",
  accent: "#c33e3a",
  mint: "#1ea97c",
  amber: "#ffb347",
  lilac: "#9c6ade",
  grid: "#e7ecf7",
  text: "#2f344b",
};

const KPI_CARD_COLORS = [
  { from: "#c8d6ff", to: "#f6f8ff", border: "#cdd9ff", accent: "#4c6fff" },
  { from: "#c3f1d2", to: "#f2fff7", border: "#b7e6c7", accent: "#1e9d58" },
  { from: "#ffe7c3", to: "#fff9ef", border: "#f7d9a6", accent: "#b76a00" },
  { from: "#c9e8ff", to: "#f1f8ff", border: "#c8e7ff", accent: "#2f6dbe" },
  { from: "#ffd8d3", to: "#fff2f0", border: "#f1c1c1", accent: "#c33e3a" },
];

const AXIS_TICK = { fontSize: 12, fill: CHART_THEME.text, fontWeight: 700 };
const TOOLTIP_STYLE = {
  backgroundColor: "#fff",
  border: "1px solid #e4e8f4",
  borderRadius: 12,
  boxShadow: "0 18px 48px rgba(17, 36, 94, 0.08)",
  padding: "12px 14px",
};
const TOOLTIP_LABEL_STYLE = { fontWeight: 800, color: "#1f2430" };
const TOOLTIP_ITEM_STYLE = { color: "#3d4353", fontWeight: 600 };
const LEGEND_STYLE = { paddingTop: 8, fontWeight: 700, color: "#3d4353" };
const DATE_PRESETS = [
  { key: "lifetime", label: "Lifetime" },
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "last-90-days", label: "Last 90 days" },
  { key: "custom", label: "Custom" },
];

const formatCurrency = (value, currency = "KWD") => {
  if (value === null || value === undefined) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;
  return `${currency} ${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "—";
  return `${(numeric * 100).toFixed(1)}%`;
};

const formatDays = (value) => {
  if (value === null || value === undefined) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "—";
  return `${numeric.toFixed(1)} days`;
};

const formatCount = (value) => {
  if (value === null || value === undefined) return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "—";
  return numeric.toLocaleString();
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const startOfDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const endOfDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(23, 59, 59, 999);
  return date;
};

const buildPresetRange = (preset) => {
  const now = new Date();
  switch (preset) {
    case "this-month": {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfDay(from), to: endOfDay(now) };
    }
    case "last-month": {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(from), to: endOfDay(to) };
    }
    case "last-90-days": {
      const from = new Date(now);
      from.setDate(from.getDate() - 89);
      return { from: startOfDay(from), to: endOfDay(now) };
    }
    case "lifetime":
      return { from: null, to: null };
    default:
      return { from: null, to: null };
  }
};

const formatRangeLabel = (range) => {
  const format = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };
  if (range?.preset === "lifetime" && !range.from && !range.to) return "Lifetime (all dates)";
  const fromLabel = format(range?.from) || "—";
  const toLabel = format(range?.to) || "—";
  return `${fromLabel} to ${toLabel}`;
};

function ReportsPage({ config: _config }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [risks, setRisks] = useState(null);
  const [concentration, setConcentration] = useState(null);
  const [watchlists, setWatchlists] = useState(null);
  const [selectedRange, setSelectedRange] = useState("lifetime");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [appliedRange, setAppliedRange] = useState(() => ({ preset: "lifetime", from: null, to: null }));
  const [rangeError, setRangeError] = useState("");

  const rangeParams = useMemo(() => {
    const params = { preset: appliedRange.preset };
    if (appliedRange.from) params.from = appliedRange.from.toISOString();
    if (appliedRange.to) params.to = appliedRange.to.toISOString();
    return params;
  }, [appliedRange]);

  const applyPresetRange = (presetKey) => {
    const presetRange = buildPresetRange(presetKey);
    setAppliedRange({ preset: presetKey, ...presetRange });
  };

  const activateCustomRange = () => {
    if (selectedRange !== "custom") {
      setSelectedRange("custom");
      setRangeError("");
    }
  };

  const handleRangeSelect = (presetKey) => {
    setSelectedRange(presetKey);
    setRangeError("");
    if (presetKey !== "custom") {
      setCustomRange({ from: "", to: "" });
      applyPresetRange(presetKey);
    }
  };

  const handleCustomDateChange = (key) => (event) => {
    const value = event.target.value;
    activateCustomRange();
    setCustomRange((prev) => ({ ...prev, [key]: value }));
  };

  const applyCustomRange = () => {
    setRangeError("");
    const fromDate = startOfDay(customRange.from);
    const toDate = endOfDay(customRange.to);
    if (!fromDate || !toDate) {
      setRangeError("Select both start and end dates.");
      return;
    }
    if (fromDate > toDate) {
      setRangeError("Start date must be on or before the end date.");
      return;
    }
    setAppliedRange({ preset: "custom", from: fromDate, to: toDate });
  };

  const activeRangeLabel = useMemo(() => formatRangeLabel(appliedRange), [appliedRange]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [summaryData, trendData, riskData, concentrationData, watchlistData] = await Promise.all([
          fetchReportSummary(rangeParams),
          fetchReportTrends(rangeParams),
          fetchReportRisks(rangeParams),
          fetchReportConcentration(rangeParams),
          fetchReportWatchlists(rangeParams),
        ]);
        if (cancelled) return;
        setSummary(summaryData);
        setTrends(trendData);
        setRisks(riskData);
        setConcentration(concentrationData);
        setWatchlists(watchlistData);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Unable to load reports.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [rangeParams]);

  const kpiCards = useMemo(() => {
    const kpis = summary?.kpis || {};
    return [
      { key: "revenue", label: "Revenue (range)", value: formatCurrency(kpis.revenue), description: "Total invoiced revenue in the selected range." },
      {
        key: "gm",
        label: "Gross margin",
        value: formatCurrency(kpis.grossMargin),
        hint: formatPercent(kpis.grossMarginRate),
        description: "Revenue minus product costs; includes gross margin percentage.",
      },
      { key: "outstanding", label: "Outstanding AR", value: formatCurrency(kpis.outstanding), description: "Unpaid balances across all open invoices." },
      { key: "overdue", label: "Overdue AR", value: formatCurrency(kpis.overdue), description: "Portion of AR that is past its due date." },
      { key: "inventoryValue", label: "Inventory value", value: formatCurrency(kpis.inventoryValue), description: "On-hand stock value using default price." },
      { key: "coverage", label: "Coverage", value: formatDays(kpis.coverageDays), description: "Projected days of stock left at the recent consumption rate." },
      { key: "customers", label: "Top 5 customers share", value: formatPercent(kpis.customerTopShare), description: "Share of revenue concentrated in your top 5 customers." },
      { key: "suppliers", label: "Top 5 suppliers share", value: formatPercent(kpis.supplierTopShare), description: "Share of inventory value tied to your top 5 suppliers." },
      { key: "totalProducts", label: "Total products", value: formatCount(kpis.totalProducts ?? kpis.productCount), description: "Active products in the catalog." },
      { key: "totalQuantity", label: "Total quantity", value: formatCount(kpis.totalQuantity), description: "Units on hand across all products." },
      { key: "reorder", label: "Below reorder", value: kpis.belowReorder ?? "—", description: "Active products at or below their reorder level." },
    ];
  }, [summary]);

  const latestStatusPeriod = useMemo(() => {
    const rows = trends?.invoiceStatus || [];
    return rows[rows.length - 1] || null;
  }, [trends]);

  const renderPipelineStatuses = () => {
    if (!latestStatusPeriod?.statuses?.length) return <p className="muted small">No invoices in range.</p>;
    return (
      <ul className="mini-list">
        {latestStatusPeriod.statuses.map((entry) => (
          <li key={entry.status} className="mini-row">
            <span>{entry.status || "unspecified"}</span>
            <span className="muted small">
              {entry.count} · {formatCurrency(entry.balance)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const renderRevenueTrend = () => {
    if (!trends?.revenueMargin?.length) return <p className="muted small">No revenue data for the range.</p>;
    const data = trends.revenueMargin.map((row) => ({
      period: row.period,
      revenue: Number(row.revenue) || 0,
      marginRate: row.grossMarginRate != null ? Number(row.grossMarginRate * 100) : null,
    }));
    return (
      <div className="chart-card">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data} margin={{ left: 4, right: 4 }}>
            <defs>
              <linearGradient id="revenueGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={CHART_THEME.primary} stopOpacity={0.95} />
                <stop offset="100%" stopColor="#7c91ff" stopOpacity={0.95} />
              </linearGradient>
              <linearGradient id="gmGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={CHART_THEME.accent} stopOpacity={0.95} />
                <stop offset="100%" stopColor="#ff9b73" stopOpacity={0.9} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
            <XAxis dataKey="period" tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
            <YAxis yAxisId="left" tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v) => `${v}%`}
              tick={AXIS_TICK}
              axisLine={{ stroke: "#e3e8f4" }}
              tickLine={false}
            />
            <Tooltip
              cursor={{ stroke: CHART_THEME.primary, strokeOpacity: 0.16, strokeWidth: 2 }}
              contentStyle={TOOLTIP_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              formatter={(value, name) => {
                if (name === "marginRate") return [`${Number(value).toFixed(1)}%`, "GM %"];
                return [formatCurrency(value), "Revenue"];
              }}
            />
            <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              name="Revenue"
              stroke="url(#revenueGradient)"
              strokeWidth={3}
              dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: CHART_THEME.primary }}
              activeDot={{ r: 6, strokeWidth: 3, fill: "#fff", stroke: CHART_THEME.primary }}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="marginRate"
              name="GM %"
              stroke="url(#gmGradient)"
              strokeWidth={3}
              dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: CHART_THEME.accent }}
              activeDot={{ r: 6, strokeWidth: 3, fill: "#fff", stroke: CHART_THEME.accent }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderTopList = (items, labelKey = "name", valueKey = "revenue", className = "") => {
    if (!items?.length) return <p className="muted small">No data.</p>;
    const total = items.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
    return (
      <ul className={`mini-list${className ? ` ${className}` : ""}`}>
        {items.map((item) => {
          const share = total > 0 ? ((Number(item[valueKey]) || 0) / total) * 100 : 0;
          return (
            <li key={item[labelKey] || item.id} className="mini-row">
              <div>
                <div className="mini-title">{item[labelKey] || "Unspecified"}</div>
                <div className="mini-subtitle">{formatCurrency(item[valueKey])}</div>
              </div>
              <span className="muted small">{share.toFixed(1)}%</span>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderWatchlist = (items, formatter, className = "") => {
    if (!items?.length) return <p className="muted small">All clear.</p>;
    return (
      <ul className={`mini-list${className ? ` ${className}` : ""}`}>
        {items.map((item) => (
          <li key={item.productId || item.invoiceId || item.name} className="mini-row">
            {formatter(item)}
          </li>
        ))}
      </ul>
    );
  };

const renderStatValue = (value) => {
  const str = typeof value === "number" ? String(value) : value;
  if (typeof str === "string") {
    const [maybeCurrency, ...rest] = str.trim().split(/\s+/);
    const amount = rest.join(" ");
      if (/^[A-Za-z]{3}$/.test(maybeCurrency) && amount) {
        return (
          <>
            <span className="stat-currency">{maybeCurrency}</span>
            <span className="stat-number">{amount}</span>
          </>
        );
      }
    }
    return value;
  };

  return (
    <div className="reports-layout">
      <section className="panel wide reports-panel">
        <div className="reports-filter-header">
          <div className="reports-filter-row">
            <div className="filters-scroll">
              <FilterBar options={DATE_PRESETS} selected={selectedRange} onSelect={handleRangeSelect} />
            </div>
            <div className="muted small active-range-label compact">Range: {activeRangeLabel}</div>
          </div>
          <div className="range-controls slim inline">
            <div className={`custom-range ${selectedRange === "custom" ? "active" : ""}`}>
              <div className="custom-range-fields">
                <label htmlFor="reports-range-from">From</label>
                <input
                  id="reports-range-from"
                  type="date"
                  value={customRange.from}
                  onFocus={activateCustomRange}
                  onChange={handleCustomDateChange("from")}
                />
                <label htmlFor="reports-range-to">To</label>
                <input
                  id="reports-range-to"
                  type="date"
                  value={customRange.to}
                  onFocus={activateCustomRange}
                  onChange={handleCustomDateChange("to")}
                />
              </div>
              <button className="ghost-btn small" type="button" onClick={applyCustomRange}>
                Apply
              </button>
            </div>
            {rangeError ? <p className="muted danger extra-small">{rangeError}</p> : null}
          </div>
        </div>

        {loading ? <p className="muted">Loading reports…</p> : null}
        {error ? <p className="muted danger">{error}</p> : null}

        {!loading && !error ? (
          <>
            <div className="stat-grid reports-kpi-grid">
              {kpiCards.map((card, index) => {
                const palette = KPI_CARD_COLORS[index % KPI_CARD_COLORS.length];
                return (
                  <article
                    key={card.key}
                    className="stat-card reports-stat-card"
                    style={{
                      "--stat-from": palette.from,
                      "--stat-to": palette.to,
                      "--stat-border": palette.border,
                      "--stat-accent": palette.accent,
                    }}
                  >
                    <div className="stat-label">{card.label}</div>
                    <div className="stat-value">{renderStatValue(card.value)}</div>
                    {card.hint ? <div className="stat-hint">{card.hint}</div> : null}
                    {card.description ? <div className="stat-desc">{card.description}</div> : null}
                  </article>
                );
              })}
            </div>

            <div className="reports-sections">
              <div className="reports-section">
                <header className="section-head">
                  <h3>Revenue & margin trend</h3>
                  <p className="muted small">Monthly revenue with gross margin rate.</p>
                </header>
                {renderRevenueTrend()}
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Invoice pipeline (latest period)</h3>
                    <p className="muted small">
                      Status mix for {latestStatusPeriod?.period || "current range"}.
                    </p>
                  </header>
                  {renderPipelineStatuses()}
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>AR aging buckets</h3>
                    <p className="muted small">Balances grouped by days past due.</p>
                  </header>
                  {summary?.agingBuckets?.length ? (
                    <div className="chart-card small">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart
                          data={summary.agingBuckets.map((bucket) => ({
                            bucket: bucket.bucket,
                            balance: Number(bucket.balance) || 0,
                          }))}
                          barCategoryGap={12}
                          margin={{ left: -4, right: 8 }}
                        >
                          <defs>
                            <linearGradient id="agingGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={CHART_THEME.primary} stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#7c91ff" stopOpacity={0.75} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
                          <XAxis dataKey="bucket" tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                          <YAxis
                            tickFormatter={(v) => `${v / 1000}k`}
                            tick={AXIS_TICK}
                            axisLine={{ stroke: "#e3e8f4" }}
                            tickLine={false}
                          />
                          <Tooltip
                            cursor={{ fill: "rgba(76, 111, 255, 0.06)" }}
                            contentStyle={TOOLTIP_STYLE}
                            labelStyle={TOOLTIP_LABEL_STYLE}
                            itemStyle={TOOLTIP_ITEM_STYLE}
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Bar dataKey="balance" fill="url(#agingGradient)" radius={0} maxBarSize={38} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="muted small">No open balances.</p>
                  )}
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Stock value by category</h3>
                    <p className="muted small">Inventory value and cost by category.</p>
                  </header>
                  {summary?.stockValueByCategory?.length ? (
                    <div className="chart-card small">
                      <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: Math.max(summary.stockValueByCategory.length * 140, 640) }}>
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={summary.stockValueByCategory} barCategoryGap={14} margin={{ left: -6, right: 12 }}>
                              <defs>
                                <linearGradient id="categoryValue" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={CHART_THEME.primary} stopOpacity={0.95} />
                                  <stop offset="100%" stopColor="#7c91ff" stopOpacity={0.7} />
                                </linearGradient>
                                <linearGradient id="categoryCost" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={CHART_THEME.mint} stopOpacity={0.95} />
                                  <stop offset="100%" stopColor="#4ed3a2" stopOpacity={0.7} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
                              <XAxis dataKey="category" tick={{ ...AXIS_TICK, fontSize: 11 }} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                              <YAxis
                                tickFormatter={(v) => `${v / 1000}k`}
                                tick={AXIS_TICK}
                                axisLine={{ stroke: "#e3e8f4" }}
                                tickLine={false}
                              />
                              <Tooltip
                                cursor={{ fill: "rgba(76, 111, 255, 0.04)" }}
                                contentStyle={TOOLTIP_STYLE}
                                labelStyle={TOOLTIP_LABEL_STYLE}
                                itemStyle={TOOLTIP_ITEM_STYLE}
                                formatter={(value) => formatCurrency(value)}
                              />
                              <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" />
                              <Bar dataKey="value" name="Value" fill="url(#categoryValue)" radius={0} maxBarSize={44} />
                              <Bar dataKey="cost" name="Cost" fill="url(#categoryCost)" radius={0} maxBarSize={44} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="muted small">No inventory data.</p>
                  )}
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>Stock quantity by category</h3>
                    <p className="muted small">Units on hand grouped by category.</p>
                  </header>
                  {summary?.stockQuantityByCategory?.length ? (
                    <div className="chart-card small">
                      <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: Math.max(summary.stockQuantityByCategory.length * 140, 640) }}>
                          <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={summary.stockQuantityByCategory} barCategoryGap={14} margin={{ left: -6, right: 12 }}>
                              <defs>
                                <linearGradient id="categoryQuantity" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={CHART_THEME.amber} stopOpacity={0.95} />
                                  <stop offset="100%" stopColor="#ffcf85" stopOpacity={0.7} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
                              <XAxis dataKey="category" tick={{ ...AXIS_TICK, fontSize: 11 }} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                              <YAxis tickFormatter={(v) => formatCount(v)} tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                              <Tooltip
                                cursor={{ fill: "rgba(255, 179, 71, 0.06)" }}
                                contentStyle={TOOLTIP_STYLE}
                                labelStyle={TOOLTIP_LABEL_STYLE}
                                itemStyle={TOOLTIP_ITEM_STYLE}
                                formatter={(value) => formatCount(value)}
                              />
                              <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" />
                              <Bar dataKey="quantity" name="Quantity" fill="url(#categoryQuantity)" radius={0} maxBarSize={44} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="muted small">No inventory quantity data.</p>
                  )}
                </div>
              </div>

              <div className="reports-section">
                <header className="section-head">
                  <h3>Top 10 products by stock value</h3>
                  <p className="muted small">Capital tied up in inventory.</p>
                </header>
                <div className="table-wrap">
                  <Table
                    hideViewAction
                    enableSelection={false}
                    columns={[
                      { key: "name", label: "Product", sortable: false },
                      { key: "value", label: "Value", align: "right" },
                      { key: "costValue", label: "Cost", align: "right" },
                      { key: "quantityOnHand", label: "Qty", align: "right" },
                    ]}
                    rows={(summary?.stockValueTopProducts || []).map((row) => ({
                      ...row,
                      value: { display: formatCurrency(row.value), sortValue: toNumberOrNull(row.value) },
                      costValue: { display: formatCurrency(row.costValue), sortValue: toNumberOrNull(row.costValue) },
                      quantityOnHand: { display: formatCount(row.quantityOnHand), sortValue: toNumberOrNull(row.quantityOnHand) },
                    }))}
                  />
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Working capital</h3>
                    <p className="muted small">Inventory value vs outstanding receivables.</p>
                  </header>
                  {trends?.workingCapital?.length ? (
                    <div className="chart-card">
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={trends.workingCapital} margin={{ left: 4, right: 4 }}>
                          <defs>
                            <linearGradient id="inventoryLine" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor={CHART_THEME.mint} stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#48d7aa" stopOpacity={0.9} />
                            </linearGradient>
                            <linearGradient id="outstandingLine" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor={CHART_THEME.primary} stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#7c91ff" stopOpacity={0.95} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
                          <XAxis dataKey="period" tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                          <YAxis
                            tickFormatter={(v) => `${v / 1000}k`}
                            tick={AXIS_TICK}
                            axisLine={{ stroke: "#e3e8f4" }}
                            tickLine={false}
                          />
                          <Tooltip
                            cursor={{ stroke: CHART_THEME.primary, strokeOpacity: 0.16, strokeWidth: 2 }}
                            contentStyle={TOOLTIP_STYLE}
                            labelStyle={TOOLTIP_LABEL_STYLE}
                            itemStyle={TOOLTIP_ITEM_STYLE}
                            formatter={(value, name) => [
                              formatCurrency(value),
                              name === "inventoryValue" ? "Inventory" : "Outstanding",
                            ]}
                          />
                          <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" />
                          <Line
                            type="monotone"
                            dataKey="inventoryValue"
                            name="Inventory"
                            stroke="url(#inventoryLine)"
                            strokeWidth={3}
                            dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: CHART_THEME.mint }}
                            activeDot={{ r: 6, strokeWidth: 3, fill: "#fff", stroke: CHART_THEME.mint }}
                          />
                          <Line
                            type="monotone"
                            dataKey="outstanding"
                            name="Outstanding"
                            stroke="url(#outstandingLine)"
                            strokeWidth={3}
                            dot={{ r: 4, strokeWidth: 2, fill: "#fff", stroke: CHART_THEME.primary }}
                            activeDot={{ r: 6, strokeWidth: 3, fill: "#fff", stroke: CHART_THEME.primary }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="muted small">No working capital data.</p>
                  )}
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>Supplier dependency & margin</h3>
                    <p className="muted small">Top suppliers by inventory value and revenue margin.</p>
                  </header>
                  <div className="reports-split nested">
                    <div>
                      <div className="chart-label">Inventory dependency</div>
                      {renderTopList(concentration?.suppliers, "name", "value") }
                    </div>
                    <div>
                      <div className="chart-label">Sales & margin</div>
                      {renderTopList(concentration?.supplierSalesMargin, "name", "revenue") }
                    </div>
                  </div>
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Customer & category margin</h3>
                    <p className="muted small">Revenue and margin by customer and category.</p>
                  </header>
                  <div className="reports-split nested">
                    <div>
                      <div className="chart-label">Top customers</div>
                      {renderTopList(concentration?.customers, "name", "revenue") }
                    </div>
                    <div>
                      <div className="chart-label">By category</div>
                      {renderTopList(concentration?.categoryRevenueMargin, "category", "revenue") }
                    </div>
                  </div>
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>Fast & slow movers</h3>
                    <p className="muted small">Units sold in the selected range vs idle stock.</p>
                  </header>
                  <div className="reports-split nested">
                    <div>
                      <div className="chart-label">Fast movers</div>
                      {renderTopList(risks?.fastMovers, "name", "units", "watchlist-scrollable")}
                    </div>
                    <div>
                      <div className="chart-label">Slow movers</div>
                      {renderTopList(risks?.slowMovers, "name", "quantityOnHand", "watchlist-scrollable")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Reorder & incidents by supplier</h3>
                    <p className="muted small">Suppliers with most items below reorder.</p>
                  </header>
                  {renderWatchlist(watchlists?.reorderBySupplier, (item) => (
                    <>
                      <div>
                        <div className="mini-title">{item.name}</div>
                        <div className="mini-subtitle">Reorder items</div>
                      </div>
                      <div className="mini-meta">
                        <span className="pill">{item.count}</span>
                      </div>
                    </>
                  )) }
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>Stock adjustments</h3>
                    <p className="muted small">Adjustments by reason (units).</p>
                  </header>
                  {risks?.adjustmentsByReason?.length ? (
                    <div className="chart-card small">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={risks.adjustmentsByReason} barCategoryGap={12} margin={{ left: -4, right: 8 }}>
                          <defs>
                            <linearGradient id="adjustmentsGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={CHART_THEME.accent} stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#ff9b73" stopOpacity={0.8} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 6" stroke={CHART_THEME.grid} vertical={false} />
                          <XAxis dataKey="reason" tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                          <YAxis tick={AXIS_TICK} axisLine={{ stroke: "#e3e8f4" }} tickLine={false} />
                          <Tooltip
                            cursor={{ fill: "rgba(195, 62, 58, 0.06)" }}
                            contentStyle={TOOLTIP_STYLE}
                            labelStyle={TOOLTIP_LABEL_STYLE}
                            itemStyle={TOOLTIP_ITEM_STYLE}
                          />
                          <Bar dataKey="units" name="Units" fill="url(#adjustmentsGradient)" radius={0} maxBarSize={42} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="muted small">No adjustments recorded.</p>
                  )}
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Top customers</h3>
                    <p className="muted small">Revenue concentration.</p>
                  </header>
                  {renderTopList(concentration?.customers, "name", "revenue")}
                </div>
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Top suppliers (inventory value)</h3>
                    <p className="muted small">Dependency on suppliers.</p>
                  </header>
                  {renderTopList(concentration?.suppliers, "name", "value")}
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Reorder watchlist</h3>
                    <p className="muted small">Items at or below reorder levels.</p>
                  </header>
                  {renderWatchlist(
                    watchlists?.reorder,
                    (item) => (
                      <>
                        <div>
                          <div className="mini-title">{item.name}</div>
                          <div className="mini-subtitle">{item.supplierName || "Unassigned"}</div>
                        </div>
                        <div className="mini-meta">
                          <span className="pill">On hand {item.quantityOnHand ?? "—"}</span>
                          <span className="pill">Reorder {item.reorderLevel ?? "—"}</span>
                          <span className="pill">Suggest {item.reorderSuggestion ?? "—"}</span>
                        </div>
                      </>
                    ),
                    "watchlist-scrollable"
                  )}
                </div>
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Overdue invoices</h3>
                    <p className="muted small">Past due balances to chase.</p>
                  </header>
                  {renderWatchlist(watchlists?.overdueInvoices, (item) => (
                    <>
                      <div>
                        <div className="mini-title">{item.invoiceNumber}</div>
                        <div className="mini-subtitle">{item.customerName || "Unspecified"}</div>
                      </div>
                      <div className="mini-meta">
                        <span className="pill">{formatCurrency(item.balance)}</span>
                        <span className="pill muted">
                          Due {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : "n/a"}
                        </span>
                      </div>
                    </>
                  ))}
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Expiry risk (next 120 days)</h3>
                    <p className="muted small">Units and value expiring soon.</p>
                  </header>
                  {renderWatchlist(watchlists?.expiry, (item) => (
                    <>
                      <div>
                        <div className="mini-title">{item.name}</div>
                        <div className="mini-subtitle">{item.supplierName || "Unassigned"}</div>
                      </div>
                      <div className="mini-meta">
                        <span className="pill">In {Math.round(item.daysToExpiry ?? 0)} days</span>
                        <span className="pill">Qty {item.quantityOnHand ?? "—"}</span>
                        <span className="pill">{formatCurrency(item.value)}</span>
                      </div>
                    </>
                  ))}
                </div>

                <div className="reports-section">
                  <header className="section-head">
                    <h3>Margin leakage</h3>
                    <p className="muted small">Lines sold below target margin.</p>
                  </header>
                  {renderWatchlist(risks?.marginLeakage, (item) => (
                    <>
                      <div>
                        <div className="mini-title">{item.description || item.productName || "Line item"}</div>
                        <div className="mini-subtitle">
                          {item.invoiceNumber} · {item.customerName || "Unspecified"}
                        </div>
                      </div>
                      <div className="mini-meta">
                        <span className="pill danger">{formatCurrency(item.margin)}</span>
                        <span className="pill muted">{formatPercent(item.marginRate)}</span>
                      </div>
                    </>
                  ))}
                </div>
              </div>

              <div className="reports-split">
                <div className="reports-section">
                  <header className="section-head">
                    <h3>Coverage risk</h3>
                    <p className="muted small">Items projected to run out soon based on recent usage.</p>
                  </header>
                  {renderWatchlist(risks?.coverageAtRisk, (item) => (
                    <>
                      <div>
                        <div className="mini-title">{item.name}</div>
                        <div className="mini-subtitle">{item.supplierName || "Unassigned"}</div>
                      </div>
                      <div className="mini-meta">
                        <span className="pill">
                          Coverage {item.coverageDays !== null && item.coverageDays !== undefined ? Math.round(item.coverageDays) : "—"}d
                        </span>
                        <span className="pill">On hand {item.quantityOnHand ?? "—"}</span>
                        {item.reorderLevel !== undefined && item.reorderLevel !== null ? (
                          <span className="pill muted">Reorder {item.reorderLevel}</span>
                        ) : null}
                      </div>
                    </>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </section>

    </div>
  );
}

export default ReportsPage;
